import { useMemo, useState } from "react";
import { ChevronRight, Loader2, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { buildStageGroups, type PickerPipeline } from "@/lib/stageMovePicker";

export interface StageMoveTarget {
  pipelineId: number;
  stageId: number;
}

interface StageMovePickerProps {
  pipelines: PickerPipeline[];
  currentPipelineId: number | null | undefined;
  currentStageId: number | null | undefined;
  selected: StageMoveTarget | null;
  onSelect: (target: StageMoveTarget) => void;
  loading?: boolean;
}

/**
 * One search across every pipeline the project may move to, with stages
 * grouped by pipeline. The current pipeline starts open; the others are
 * collapsed headers, so a pipeline change never hides below a long list.
 */
export function StageMovePicker({
  pipelines,
  currentPipelineId,
  currentStageId,
  selected,
  onSelect,
  loading = false,
}: StageMovePickerProps) {
  const [query, setQuery] = useState("");
  const [expandedIds, setExpandedIds] = useState<Set<number>>(
    () => new Set(currentPipelineId ? [currentPipelineId] : [])
  );

  const groups = useMemo(
    () =>
      buildStageGroups({ pipelines, currentPipelineId, currentStageId, query, expandedIds }),
    [pipelines, currentPipelineId, currentStageId, query, expandedIds]
  );

  const toggle = (pipelineId: number) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(pipelineId)) next.delete(pipelineId);
      else next.add(pipelineId);
      return next;
    });

  return (
    <div className="flex flex-col gap-3 min-h-0">
      <label className="flex items-center gap-2 rounded-lg border border-border px-3 h-11 focus-within:border-primary">
        <Search className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
        <span className="sr-only">Search stages in every pipeline</span>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={pipelines.length > 1 ? "Search any stage in any pipeline" : "Search stages"}
          className="flex-1 bg-transparent text-sm outline-none"
          autoFocus
        />
      </label>

      {loading && (
        <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading pipelines…
        </div>
      )}

      <div className="flex flex-col gap-2">
        {groups.map((group) => (
          <section key={group.pipeline.id} className="rounded-lg border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => toggle(group.pipeline.id)}
              aria-expanded={group.open}
              className="flex w-full items-center justify-between gap-2 px-3 min-h-12 bg-muted text-left"
            >
              <span className="flex items-center gap-2 min-w-0">
                <ChevronRight
                  className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", group.open && "rotate-90")}
                  aria-hidden="true"
                />
                <span className="text-sm font-semibold truncate">{group.pipeline.name}</span>
                {group.isCurrentPipeline && (
                  <span className="text-[11px] font-semibold text-primary bg-primary-light rounded-full px-2 py-0.5 shrink-0">
                    Current
                  </span>
                )}
              </span>
              <span className="text-xs text-muted-foreground shrink-0">{group.countLabel}</span>
            </button>

            {group.open && (
              <div className="flex flex-col p-1">
                {group.rows.map((row) => {
                  const isSelected =
                    selected?.pipelineId === group.pipeline.id && selected?.stageId === row.stage.id;
                  return (
                    <button
                      key={row.stage.id}
                      type="button"
                      disabled={row.isCurrent}
                      onClick={() => onSelect({ pipelineId: group.pipeline.id, stageId: row.stage.id })}
                      className={cn(
                        "flex items-center justify-between gap-2 min-h-11 px-3 rounded-md border text-left text-sm transition-colors",
                        isSelected
                          ? "border-primary bg-primary-light"
                          : row.isCurrent
                            ? "border-transparent bg-primary-light cursor-default"
                            : "border-transparent hover:bg-muted"
                      )}
                    >
                      <span className="flex items-center gap-3 min-w-0">
                        <span className="w-5 text-xs text-muted-foreground shrink-0">{row.position}</span>
                        <span className="font-medium truncate">{row.stage.name}</span>
                      </span>
                      {row.isCurrent && (
                        <span className="text-[11px] font-bold text-primary shrink-0">CURRENT</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        ))}

        {!loading && groups.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {query.trim() ? `No stage matches “${query.trim()}”.` : "No stages available."}
          </p>
        )}
      </div>
    </div>
  );
}
